<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Familias Inscritas - Torneo Infantil - Juvenil Primera Etapa</span>
          <v-spacer></v-spacer>
        </v-card-title>

        <v-row class="px-3 mb-2" align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              label="Buscar por nombre"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
              @input="onSearchInput"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="8" class="text-sm-right">
            <v-btn
              color="red darken-1"
              dark
              class="mr-2"
              :loading="exportingPdf"
              :disabled="exportingExcel"
              @click="exportPdf"
            >
              <v-icon small left>mdi-file-pdf-box</v-icon>
              Reporte PDF
            </v-btn>
            <v-btn
              color="green darken-1"
              dark
              :loading="exportingExcel"
              :disabled="exportingPdf"
              @click="exportExcel"
            >
              <v-icon small left>mdi-file-excel</v-icon>
              Reporte Excel
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="families"
          :loading="loading"
          :options.sync="options"
          :server-items-length="totalCount"
          :footer-props="{ itemsPerPageOptions: [10], disableItemsPerPage: true }"
          item-key="id"
          no-data-text="No hay familias registradas"
          class="elevation-1"
        >
          <template v-slot:item.total_members="{ item }">
            <v-chip small color="primary" text-color="white">{{ item.total_members }}</v-chip>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small class="mr-1" v-bind="attrs" v-on="on" @click="showDetails(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Ver detalles</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :loading="generatingBadgeId === item.id"
                  @click="downloadBadge(item)"
                >
                  <v-icon small>mdi-card-account-details</v-icon>
                </v-btn>
              </template>
              <span>Descargar gafete PDF</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-dialog v-model="dialogDetails" max-width="650px" scrollable>
      <v-card v-if="selectedFamily">
        <v-card-title class="primary--text">
          {{ selectedFamily.full_name }}
        </v-card-title>
        <v-card-subtitle>
          {{ selectedFamily.phone }} · {{ selectedFamily.email }}
        </v-card-subtitle>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text text-uppercase">Club</div>
              <div class="font-weight-medium">{{ selectedFamily.club || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text text-uppercase">Registrado</div>
              <div class="font-weight-medium">{{ formatDateTime(selectedFamily.created_at) }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text text-uppercase">Total integrantes</div>
              <div class="font-weight-medium">{{ selectedFamily.total_members }}</div>
            </v-col>
          </v-row>

          <div v-if="selectedFamily.children && selectedFamily.children.length" class="mb-4">
            <div class="font-weight-bold mb-1">Niños</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Edad</th>
                  <th>Género</th>
                  <th>Relación</th>
                  <th>Asistió</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="child in selectedFamily.children" :key="child.id">
                  <td>{{ child.full_name }}</td>
                  <td>{{ child.age !== null && child.age !== undefined ? child.age : '-' }}</td>
                  <td>{{ genderLabel(child.gender) }}</td>
                  <td>{{ child.relationship || '-' }}</td>
                  <td>
                    <v-icon small :color="child.attended ? 'success' : 'grey'">
                      {{ child.attended ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div v-if="selectedFamily.guests && selectedFamily.guests.length">
            <div class="font-weight-bold mb-1">Invitados</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Relación</th>
                  <th>Asistió</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="guest in selectedFamily.guests" :key="guest.id">
                  <td>{{ guest.full_name }}</td>
                  <td>{{ guest.relationship || '-' }}</td>
                  <td>
                    <v-icon small :color="guest.attended ? 'success' : 'grey'">
                      {{ guest.attended ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div
            v-if="(!selectedFamily.children || !selectedFamily.children.length) && (!selectedFamily.guests || !selectedFamily.guests.length)"
            class="grey--text"
          >
            Sin integrantes adicionales
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            :loading="generatingBadgeId === selectedFamily.id"
            @click="downloadBadge(selectedFamily)"
          >
            <v-icon small left>mdi-card-account-details</v-icon>
            Descargar gafete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDetails = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000" top>
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import JSPDF from 'jspdf'
import 'jspdf-autotable'
import { kidsGolfTournamentAPI } from '@/services/kidsGolfTournament'

export default {
  name: 'KidsTournamentFamilies',
  data: () => ({
    loading: false,
    families: [],
    totalCount: 0,
    search: '',
    searchTimeout: null,
    options: {
      page: 1,
      itemsPerPage: 10
    },
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'error',
    generatingBadgeId: null,
    logoBase64: null,
    exportingPdf: false,
    exportingExcel: false,
    headers: [
      { text: 'Titular', value: 'full_name' },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Email', value: 'email' },
      { text: 'Club', value: 'club' },
      { text: 'Integrantes', value: 'total_members', align: 'center', sortable: false },
      { text: 'Registrado', value: 'created_at', sortable: false },
      { text: '', value: 'actions', sortable: false, align: 'end' }
    ],
    dialogDetails: false,
    selectedFamily: null
  }),

  watch: {
    options: {
      handler () {
        this.getFamilies()
      },
      deep: true
    }
  },

  created () {
    this.getFamilies()
  },

  methods: {
    onSearchInput () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1
        this.getFamilies()
      }, 400)
    },

    async getFamilies () {
      this.loading = true
      try {
        const params = {
          page: this.options.page,
          search: this.search || undefined
        }
        const response = await kidsGolfTournamentAPI.getKidsFamilies(params)
        this.families = (response.data.results && response.data.results.families) || []
        this.totalCount = response.data.count || 0
      } catch (error) {
        console.error('Error fetching kids tournament families:', error)
        this.showMessage('Error al obtener las familias registradas', 'error')
      } finally {
        this.loading = false
      }
    },

    showDetails (item) {
      this.selectedFamily = item
      this.dialogDetails = true
    },

    async fetchAllFamiliesForExport () {
      const pageSize = 100
      let page = 1
      let allFamilies = []
      let total = null

      do {
        const response = await kidsGolfTournamentAPI.getKidsFamilies({
          page,
          page_size: pageSize,
          search: this.search || undefined
        })
        const results = response.data.results || {}
        allFamilies = allFamilies.concat(results.families || [])
        total = response.data.count || 0
        page += 1
      } while (allFamilies.length < total)

      return allFamilies
    },

    buildExportRows (families) {
      const headers = [
        'Tipo', 'Nombre', 'Teléfono', 'Email', 'Club',
        'Integrantes', 'Edad', 'Género', 'Relación', 'Asistió', 'Registrado'
      ]
      const blank = '-'

      const rows = []
      families.forEach(family => {
        rows.push([
          'Titular',
          family.full_name || blank,
          family.phone || blank,
          family.email || blank,
          family.club || blank,
          family.total_members,
          blank, blank, blank, blank,
          this.formatDateTime(family.created_at)
        ])

        ;(family.children || []).forEach(child => {
          rows.push([
            'Niño',
            child.full_name || blank,
            blank, blank, blank, blank,
            child.age !== null && child.age !== undefined ? child.age : blank,
            this.genderLabel(child.gender),
            child.relationship || blank,
            child.attended ? 'Sí' : 'No',
            blank
          ])
        })

        ;(family.guests || []).forEach(guest => {
          rows.push([
            'Invitado',
            guest.full_name || blank,
            blank, blank, blank, blank, blank, blank,
            guest.relationship || blank,
            guest.attended ? 'Sí' : 'No',
            blank
          ])
        })
      })

      return { headers, rows }
    },

    async exportPdf () {
      this.exportingPdf = true
      try {
        const families = await this.fetchAllFamiliesForExport()
        if (!families.length) {
          this.showMessage('No hay familias para exportar', 'error')
          return
        }

        const { headers, rows } = this.buildExportRows(families)

        const doc = new JSPDF('landscape', 'pt', 'letter')
        doc.setFontSize(14)
        doc.text('Familias Inscritas - Torneo Infantil - Juvenil Primera Etapa', 40, 40)

        doc.autoTable({
          startY: 60,
          head: [headers],
          body: rows,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [21, 101, 192] },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.raw[0] === 'Titular') {
              data.cell.styles.fontStyle = 'bold'
              data.cell.styles.fillColor = [235, 241, 250]
            }
          }
        })

        doc.save(`familias_torneo_infantil_${this.exportFileDate()}.pdf`)
      } catch (error) {
        console.error('Error exporting families to PDF:', error)
        this.showMessage('Error al generar el reporte PDF', 'error')
      } finally {
        this.exportingPdf = false
      }
    },

    async exportExcel () {
      this.exportingExcel = true
      try {
        const families = await this.fetchAllFamiliesForExport()
        if (!families.length) {
          this.showMessage('No hay familias para exportar', 'error')
          return
        }

        const { headers, rows } = this.buildExportRows(families)

        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => this.csvEscape(cell)).join(','))
          .join('\r\n')

        const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `familias_torneo_infantil_${this.exportFileDate()}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error exporting families to Excel:', error)
        this.showMessage('Error al generar el reporte Excel', 'error')
      } finally {
        this.exportingExcel = false
      }
    },

    csvEscape (value) {
      const text = value === null || value === undefined ? '' : String(value)
      if (/[",\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
      }
      return text
    },

    exportFileDate () {
      return new Date().toISOString().slice(0, 10)
    },

    async downloadBadge (item) {
      this.generatingBadgeId = item.id
      try {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(item.access_code)}&ecc=M&margin=20&size=400x400`
        const [qrResponse, logoBase64] = await Promise.all([
          axios.get(qrUrl, { responseType: 'arraybuffer' }),
          this.getLogoBase64()
        ])
        const qrBase64 = `data:image/png;base64,${this.arrayBufferToBase64(qrResponse.data)}`

        const pageWidth = 320
        const pageHeight = 560
        const pdf = new JSPDF({ orientation: 'portrait', unit: 'pt', format: [pageWidth, pageHeight] })
        const centerX = pageWidth / 2

        // Header
        pdf.setFillColor(21, 101, 192)
        pdf.rect(0, 0, pageWidth, 108, 'F')

        if (logoBase64) {
          const logoBoxSize = 44
          const logoSize = 34
          const logoBoxX = centerX - (logoBoxSize / 2)
          const logoBoxY = 14
          pdf.setFillColor(255, 255, 255)
          pdf.roundedRect(logoBoxX, logoBoxY, logoBoxSize, logoBoxSize, 8, 8, 'F')
          pdf.addImage(
            logoBase64, 'PNG',
            centerX - (logoSize / 2), logoBoxY + ((logoBoxSize - logoSize) / 2),
            logoSize, logoSize
          )
        }

        pdf.setTextColor(255, 255, 255)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(13)
        pdf.text('Torneo Infantil - Juvenil Primera Etapa', centerX, 76, { align: 'center' })
        pdf.setFontSize(8.5)
        pdf.setFont('helvetica', 'normal')
        pdf.text('Club Campestre de San Luis Potosi', centerX, 92, { align: 'center' })

        // Titular
        pdf.setTextColor(40, 40, 40)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(16)
        pdf.text(item.full_name, centerX, 138, { align: 'center' })

        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(10)
        pdf.setTextColor(90, 90, 90)
        pdf.text(item.club || '', centerX, 156, { align: 'center' })

        // QR
        const qrSize = 150
        pdf.addImage(qrBase64, 'PNG', centerX - (qrSize / 2), 176, qrSize, qrSize)

        pdf.setDrawColor(220, 220, 220)
        pdf.line(30, 344, pageWidth - 30, 344)

        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(10)
        pdf.setTextColor(40, 40, 40)
        pdf.text('Integrantes', 30, 362)

        let y = 380
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(9);

        [...(item.children || []), ...(item.guests || [])].forEach(member => {
          const detail = member.age ? `${member.age} años` : (member.relationship || '')
          pdf.text(`• ${member.full_name}${detail ? ' - ' + detail : ''}`, 34, y)
          y += 14
        })

        pdf.setDrawColor(220, 220, 220)
        pdf.line(30, pageHeight - 34, pageWidth - 30, pageHeight - 34)

        pdf.setFont('helvetica', 'italic')
        pdf.setFontSize(8)
        pdf.setTextColor(130, 130, 130)
        pdf.text('Powered by Intrare', centerX, pageHeight - 18, { align: 'center' })

        pdf.save(`gafete_${this.slugify(item.full_name)}.pdf`)
      } catch (error) {
        console.error('Error generating badge PDF:', error)
        this.showMessage('Error al generar el gafete', 'error')
      } finally {
        this.generatingBadgeId = null
      }
    },

    async getLogoBase64 () {
      if (this.logoBase64) return this.logoBase64
      try {
        const response = await axios.get(`${process.env.BASE_URL}ms-icon-150x150.png`, { responseType: 'arraybuffer' })
        this.logoBase64 = `data:image/png;base64,${this.arrayBufferToBase64(response.data)}`
      } catch (error) {
        console.error('Error loading badge logo:', error)
        this.logoBase64 = null
      }
      return this.logoBase64
    },

    arrayBufferToBase64 (buffer) {
      let binary = ''
      const bytes = new Uint8Array(buffer)
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return window.btoa(binary)
    },

    slugify (text) {
      return (text || 'familia')
        .toString()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase()
    },

    showMessage (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    genderLabel (gender) {
      if (gender === 'M') return 'Masculino'
      if (gender === 'F') return 'Femenino'
      return '-'
    },

    formatDateTime (datetime) {
      if (!datetime) return '-'
      return new Date(datetime).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>
